// Booking request helpers shared by the /schedule page. The static Google Ads
// landing page (public/landing-google-ads.html) carries a plain-JS copy of the
// same rules; keep the two in step.
//
// Exactly-once bookings: the portal backend (POST /api/schedule-appointment)
// treats `booking_uuid` as the identity of ONE booking. A request that repeats
// a UUID it has already stored gets the ORIGINAL booking back
// (idempotent_replay: true) — even if the details differ — so:
//   * a retry of the SAME details reuses the same UUID (network drop, timeout,
//     503 "retry with the SAME booking_uuid", page reload, second click);
//   * any change to the details, or a new booking after a confirmed one,
//     gets a NEW UUID.
// The pending UUID is kept in sessionStorage, keyed to a fingerprint of the
// exact payload, so it survives a reload in the same tab but not a new visit.
//
// Ads conversions: counted only for a response that proves a booking was
// persisted (success:true, the UUID we sent echoed back, and an ERP Lead or
// Quotation id). The spam-trap reply ({success:true} with no ids) never
// counts. The booking UUID is the conversion transaction_id, and each UUID is
// sent at most once from this browser.

export const PENDING_KEY = 'aaa_pending_booking';
export const SENT_CONVERSIONS_KEY = 'aaa_conversions_sent';
export const CONVERSION_SEND_TO = 'AW-17797608371/J8huCPrzyNccELPnx6ZC';
const MAX_REMEMBERED_CONVERSIONS = 50;

function safeStorage(name) {
  try {
    const s = window[name];
    const probe = '__aaa_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch (e) {
    return null;
  }
}

export function newBookingUuid() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // RFC 4122 variant
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// Stable fingerprint of what is being booked (key order independent).
export function bookingFingerprint(payload) {
  const keys = Object.keys(payload || {}).filter((k) => k !== 'booking_uuid').sort();
  return JSON.stringify(keys.map((k) => [k, payload[k] === undefined ? null : payload[k]]));
}

// The UUID to send for this payload: the pending one if the details are
// identical (a retry), otherwise a fresh one that becomes the pending one.
export function bookingUuidFor(payload, memory) {
  const fp = bookingFingerprint(payload);
  const store = safeStorage('sessionStorage');
  let pending = memory && memory.pending;
  if (!pending && store) {
    try { pending = JSON.parse(store.getItem(PENDING_KEY) || 'null'); } catch (e) { pending = null; }
  }
  if (pending && pending.fp === fp && typeof pending.uuid === 'string') {
    if (memory) memory.pending = pending;
    return pending.uuid;
  }
  const next = { uuid: newBookingUuid(), fp };
  if (memory) memory.pending = next;
  if (store) { try { store.setItem(PENDING_KEY, JSON.stringify(next)); } catch (e) { /* in-memory only */ } }
  return next.uuid;
}

// After a definitive success the booking is done: the next request is new.
export function clearPendingBooking(memory) {
  if (memory) memory.pending = null;
  const store = safeStorage('sessionStorage');
  if (store) { try { store.removeItem(PENDING_KEY); } catch (e) { /* ignore */ } }
}

const nonEmpty = (v) => typeof v === 'string' && v.trim() !== '';

// True only for a response proving THIS booking was persisted in ERP.
export function isVerifiedBooking(data, sentUuid) {
  return !!data && data.success === true && nonEmpty(sentUuid) &&
    data.booking_uuid === sentUuid && (nonEmpty(data.lead) || nonEmpty(data.quotation));
}

// Whether a failed response says the same request may be retried safely.
export function isRetryable(status, data) {
  if (data && typeof data.retryable === 'boolean') return data.retryable;
  return status >= 500 || status === 409 || status === 408;
}

// Fire the Ads conversion for a verified booking, at most once per booking
// UUID from this browser. Returns true if an event was sent.
export function sendConversionOnce(bookingUuid, gtag = typeof window !== 'undefined' ? window.gtag : undefined) {
  if (!nonEmpty(bookingUuid) || typeof gtag !== 'function') return false;
  const store = safeStorage('localStorage');
  let sent = [];
  if (store) { try { sent = JSON.parse(store.getItem(SENT_CONVERSIONS_KEY) || '[]'); } catch (e) { sent = []; } }
  if (!Array.isArray(sent)) sent = [];
  if (sent.indexOf(bookingUuid) !== -1) return false;
  if (typeof window !== 'undefined') {
    window.__aaaConversionsSent = window.__aaaConversionsSent || {};
    if (window.__aaaConversionsSent[bookingUuid]) return false;
    window.__aaaConversionsSent[bookingUuid] = true;
  }
  sent.push(bookingUuid);
  if (store) { try { store.setItem(SENT_CONVERSIONS_KEY, JSON.stringify(sent.slice(-MAX_REMEMBERED_CONVERSIONS))); } catch (e) { /* ignore */ } }
  gtag('event', 'conversion', { send_to: CONVERSION_SEND_TO, transaction_id: bookingUuid });
  return true;
}

export const RETRY_MESSAGE =
  "We couldn't confirm your request yet. Please press Request Appointment again — " +
  "it is safe and will not create a second booking. Or call us at (469) 751-3567.";
